import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MatSelectModule } from '@angular/material/select';
import { NotificationService } from '../../services/notification.service'; // NotificationService;
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { PackageService } from '../../services/package.service';
import { PrepaidWashBookService } from '../../services/prepaid-wash-book.service';
import { Package } from '../../shared/types';
import {
  mapPrepaidFormToCreatePayload,
  PrepaidPackageFormData,
  PrepaidWashItemForm,
} from '../../models/prepaid-wash-book.models';
import { sanitizeErrorMessage } from '../../shared/utils/error.util';
import { ThemeColorPickerComponent } from '../../shared/components/theme-color-picker';
import { ModalFooterActionsComponent } from '../../shared/components/modal-footer-actions/modal-footer-actions.component';
import { DEFAULT_THEME_BACKGROUND } from '../../shared/utils/theme-color.util';

export type { PrepaidPackageFormData, PrepaidWashItemForm as PrepaidWashItem };

interface SelectOption {
  label: string;
  value: string;
}

interface WashNameSelectionMeta {
  package: Package;
  variant?: NonNullable<Package['variants']>[number];
}

@Component({
  selector: 'app-add-prepaid-modal',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatSelectModule,
    ThemeColorPickerComponent,
    ModalFooterActionsComponent,
  ],
  templateUrl: './add-prepaid-modal.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./add-prepaid-modal.component.scss'],
})
export class AddPrepaidModalComponent implements OnInit, OnDestroy {
  /** Cap wash line items in the UI; increase when supporting wash_c, wash_d, etc. */
  readonly maxWashItems = 2;

  @Input() prepaidData: PrepaidPackageFormData | null = null;
  /** When set, submit uses PATCH instead of POST. */
  @Input() prepaidBookId: string | null = null;
  @Output() closed = new EventEmitter<void>();
  @Output() saved = new EventEmitter<PrepaidPackageFormData>();

  private readonly fb = inject(FormBuilder);
  private readonly packageService = inject(PackageService);
  private readonly prepaidWashBookService = inject(PrepaidWashBookService);
  private readonly messageService = inject(NotificationService);

  prepaidForm: FormGroup = this.buildForm();
  submitted = false;
  isSubmitting = false;
  /** Expand/collapse state per wash section (index 0 is always expanded). */
  washSectionExpanded: boolean[] = Array.from({ length: this.maxWashItems }, () => true);

  /** Wash name dropdown options per line item index (from API). */
  washNameOptionsByIndex: SelectOption[][] = Array.from({ length: this.maxWashItems }, () => []);
  /** Lookup for wash_name value → package (+ optional variant). */
  washSelectionMetaByIndex: Map<string, WashNameSelectionMeta>[] = Array.from(
    { length: this.maxWashItems },
    () => new Map()
  );
  /** Cached package rows per line item index (from API). */
  packagesByIndex: Package[][] = Array.from({ length: this.maxWashItems }, () => []);
  /** Whether the selected package allows manual wash price entry. */
  allowsCustomPriceByIndex: boolean[] = Array.from({ length: this.maxWashItems }, () => false);
  washNamesLoading: boolean[] = Array.from({ length: this.maxWashItems }, () => false);
  /** Bumped per wash-name request so a superseded response is discarded (latest wins). */
  private washNameRequestSeq: number[] = Array.from({ length: this.maxWashItems }, () => 0);

  readonly washTypeOptions: SelectOption[] = [
    { label: 'Single Wash', value: 'single_wash' },
    { label: 'Add on', value: 'add_on' },
  ];

  private readonly destroy$ = new Subject<void>();

  ngOnInit(): void {
    if (this.prepaidData) {
      this.patchPrepaidData(this.prepaidData);
    }
    this.setupComputedTotals();
    this.setupWashNameResetOnTypeChange();
    this.setupWashPriceOnNameChange();
    this.setupOptionalWashItemValidation();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get items(): FormArray {
    return this.prepaidForm.get('items') as FormArray;
  }

  get modalTitle(): string {
    return this.isEditMode ? 'Edit Prepaid Wash Books' : 'Add Prepaid Wash Books';
  }

  get isEditMode(): boolean {
    return !!this.prepaidBookId;
  }

  getWashGroup(index: number): FormGroup {
    return this.items.at(index) as FormGroup;
  }

  getWashSectionLabel(index: number): string {
    return `Configures Wash ${this.getWashLetter(index)}`;
  }

  getWashLetter(index: number): string {
    return String.fromCharCode(65 + index);
  }

  isWashItemRequired(index: number): boolean {
    return index === 0;
  }

  isWashSectionCollapsible(index: number): boolean {
    return index > 0;
  }

  isWashSectionExpanded(index: number): boolean {
    return this.washSectionExpanded[index] ?? true;
  }

  washNameOptions(index: number): SelectOption[] {
    return this.washNameOptionsByIndex[index] ?? [];
  }

  isWashNamesLoading(index: number): boolean {
    return this.washNamesLoading[index] ?? false;
  }

  isWashNameSelectDisabled(index: number): boolean {
    return !this.getWashGroup(index).get('wash_type')?.value || this.isWashNamesLoading(index);
  }

  getWashNameFieldLabel(index: number): string {
    return this.getWashGroup(index).get('wash_type')?.value === 'add_on' ? 'Add on' : 'Wash Name';
  }

  getWashNameRequiredErrorMessage(index: number): string {
    return `${this.getWashNameFieldLabel(index)} is required`;
  }

  getWashPriceFieldLabel(index: number): string {
    return this.getWashGroup(index).get('wash_type')?.value === 'add_on'
      ? 'Add-on Price'
      : 'Wash Price';
  }

  isWashPriceEditable(index: number): boolean {
    const washName = this.getWashGroup(index).get('wash_name')?.value;
    if (!washName) {
      return true;
    }
    return this.allowsCustomPriceByIndex[index] ?? false;
  }

  getWashPricePlaceholder(index: number): string {
    return this.isWashPriceEditable(index) ? 'Enter price' : 'Auto from package';
  }

  getWashTotalFieldLabel(index: number): string {
    return `Wash ${this.getWashLetter(index)} Total Price`;
  }

  getWashTypeInputId(index: number): string {
    return `washType${index}`;
  }

  getWashNameInputId(index: number): string {
    return `washName${index}`;
  }

  getWashCountInputId(index: number): string {
    return `washCount${index}`;
  }

  getWashPriceInputId(index: number): string {
    return `washPrice${index}`;
  }

  getWashTotalInputId(index: number): string {
    return `washTotal${index}`;
  }

  getWashExpiryInputId(index: number): string {
    return `washExpiry${index}`;
  }

  toggleWashSection(index: number): void {
    if (!this.isWashSectionCollapsible(index)) {
      return;
    }
    this.washSectionExpanded[index] = !this.washSectionExpanded[index];
  }

  onOverlayClick(event: MouseEvent): void {
    if (event.target === event.currentTarget) {
      this.closeModal();
    }
  }

  closeModal(): void {
    this.closed.emit();
  }

  clearForm(): void {
    this.submitted = false;
    this.prepaidForm.reset({
      prepaid_package_name: '',
      package_status: true,
      theme_color: DEFAULT_THEME_BACKGROUND,
      total_wash_price: null,
      selling_price: null,
    });
    this.items.controls.forEach((group, index) => {
      (group as FormGroup).reset(this.getDefaultWashItemValue());
      this.applyWashItemValidators(group as FormGroup, this.isWashItemRequired(index));
    });
    this.washNameOptionsByIndex = Array.from({ length: this.maxWashItems }, () => []);
    this.washSelectionMetaByIndex = Array.from({ length: this.maxWashItems }, () => new Map());
    this.packagesByIndex = Array.from({ length: this.maxWashItems }, () => []);
    this.allowsCustomPriceByIndex = Array.from({ length: this.maxWashItems }, () => false);
    this.washNamesLoading = Array.from({ length: this.maxWashItems }, () => false);
    this.washNameRequestSeq = this.washNameRequestSeq.map(seq => seq + 1);
    this.washSectionExpanded = Array.from({ length: this.maxWashItems }, () => true);
    this.items.controls.forEach((_, index) => this.resetWashPriceControl(index));
  }

  onSubmit(): void {
    this.submitted = true;
    this.items.controls.forEach(group => group.updateValueAndValidity());
    if (this.prepaidForm.invalid) {
      this.prepaidForm.markAllAsTouched();
      return;
    }

    const formValue = this.prepaidForm.getRawValue() as PrepaidPackageFormData;
    const payload = mapPrepaidFormToCreatePayload(
      formValue,
      (index, washName) => this.getWashSelectionMeta(index, washName)?.variant
    );

    this.isSubmitting = true;
    const request$ = this.isEditMode
      ? this.prepaidWashBookService.updatePrepaidWashBook(this.prepaidBookId!, payload)
      : this.prepaidWashBookService.createPrepaidWashBook(payload);

    request$.subscribe({
      next: () => {
        this.isSubmitting = false;
        this.messageService.add({
          severity: 'success',
          summary: this.isEditMode ? 'Prepaid wash book updated!' : 'Prepaid wash book added!',
          detail: this.isEditMode
            ? `"${formValue.prepaid_package_name}" has been updated successfully.`
            : `"${formValue.prepaid_package_name}" has been created successfully.`,
        });
        this.packageService.loadPackages();
        this.saved.emit(formValue);
        this.closed.emit();
      },
      error: err => {
        this.isSubmitting = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: sanitizeErrorMessage(err.error?.message),
        });
      },
    });
  }

  showError(controlPath: string): boolean {
    const control = this.prepaidForm.get(controlPath);
    return this.isControlInvalid(control);
  }

  showWashError(index: number, controlName: string): boolean {
    const control = this.getWashGroup(index).get(controlName);
    return this.isControlInvalid(control);
  }

  showOptionalWashSectionError(index: number): boolean {
    if (index === 0) {
      return false;
    }
    const group = this.getWashGroup(index);
    return !!(
      group.errors?.['incompleteOptionalWash'] &&
      (group.dirty || group.touched || this.submitted)
    );
  }

  /** Maps UI wash type to `wash_types` query value for view-all-by-user. */
  mapWashTypeToApiParam(washType: string): string {
    if (washType === 'monthly_membership') {
      return 'monthly_wash';
    }
    return washType;
  }

  private isControlInvalid(control: ReturnType<FormGroup['get']>): boolean {
    return !!(control && control.invalid && (control.dirty || control.touched || this.submitted));
  }

  private buildForm(): FormGroup {
    const washItems = Array.from({ length: this.maxWashItems }, (_, index) =>
      this.createWashItemGroup(index)
    );

    return this.fb.group({
      prepaid_package_name: ['', Validators.required],
      package_status: [true],
      theme_color: [DEFAULT_THEME_BACKGROUND, Validators.required],
      items: this.fb.array(washItems),
      total_wash_price: [{ value: null, disabled: true }],
      selling_price: [null, Validators.required],
    });
  }

  private createWashItemGroup(index: number): FormGroup {
    const required = this.isWashItemRequired(index);
    const group = this.fb.group({
      wash_type: [null, required ? Validators.required : []],
      wash_name: [null, required ? Validators.required : []],
      number_of_washes: [null, required ? Validators.required : []],
      wash_price: [null, required ? Validators.required : []],
      wash_total: [{ value: null, disabled: true }],
      expiry_months: [null, required ? Validators.required : []],
    });

    if (!required) {
      group.setValidators(this.optionalWashItemAllOrNothingValidator());
    }

    return group;
  }

  private optionalWashItemAllOrNothingValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const group = control as FormGroup;
      const fields = ['wash_type', 'wash_name', 'number_of_washes', 'wash_price', 'expiry_months'];
      const values = fields.map(name => group.get(name)?.value);
      const isFilled = (value: unknown) => value !== null && value !== undefined && value !== '';
      const anyFilled = values.some(isFilled);
      if (!anyFilled) {
        return null;
      }
      return values.every(isFilled) ? null : { incompleteOptionalWash: true };
    };
  }

  private setupOptionalWashItemValidation(): void {
    this.items.controls.forEach((group, index) => {
      if (index === 0) {
        return;
      }
      const washGroup = group as FormGroup;
      ['wash_type', 'wash_name', 'number_of_washes', 'wash_price', 'expiry_months'].forEach(
        name => {
          washGroup
            .get(name)
            ?.valueChanges.pipe(takeUntil(this.destroy$))
            .subscribe(() => washGroup.updateValueAndValidity({ emitEvent: false }));
        }
      );
    });
  }

  private applyWashItemValidators(group: FormGroup, required: boolean): void {
    const rules = required ? [Validators.required] : [];
    ['wash_type', 'wash_name', 'number_of_washes', 'wash_price', 'expiry_months'].forEach(key => {
      const control = group.get(key);
      control?.setValidators(rules);
      control?.updateValueAndValidity({ emitEvent: false });
    });
  }

  private getDefaultWashItemValue(): PrepaidWashItemForm {
    return {
      wash_type: null,
      wash_name: null,
      number_of_washes: null,
      wash_price: null,
      wash_total: null,
      expiry_months: null,
    };
  }

  private patchPrepaidData(data: PrepaidPackageFormData): void {
    this.prepaidForm.patchValue({
      prepaid_package_name: data.prepaid_package_name,
      package_status: data.package_status,
      theme_color: data.theme_color || 'theme-1',
      total_wash_price: data.total_wash_price,
      selling_price: data.selling_price,
    });

    data.items?.forEach((item, index) => {
      if (index < this.maxWashItems) {
        this.getWashGroup(index).patchValue(item);
        if (item.wash_type) {
          this.loadWashNamesForIndex(index, item.wash_type, {
            packageId: item.wash_name,
            preservedPrice: item.wash_price,
          });
        }
      }
    });
  }

  private loadWashNamesForIndex(
    index: number,
    washType: string | null,
    options?: { packageId?: string | null; preservedPrice?: number | null }
  ): void {
    if (!washType) {
      this.washNameRequestSeq[index]++;
      this.washNameOptionsByIndex[index] = [];
      this.washSelectionMetaByIndex[index] = new Map();
      this.packagesByIndex[index] = [];
      return;
    }

    const apiWashType = this.mapWashTypeToApiParam(washType);
    const seq = ++this.washNameRequestSeq[index];
    this.washNamesLoading[index] = true;

    this.packageService
      .getPackagesByWashTypes(apiWashType)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          if (seq === this.washNameRequestSeq[index]) {
            this.washNamesLoading[index] = false;
          }
        })
      )
      .subscribe(packages => {
        if (seq !== this.washNameRequestSeq[index]) {
          return;
        }
        const activePackages = packages.filter(pkg => pkg.package_status !== false);
        this.packagesByIndex[index] = activePackages;
        const built = this.buildWashNameOptions(activePackages);
        this.washNameOptionsByIndex[index] = built.options;
        this.washSelectionMetaByIndex[index] = built.meta;
        if (options?.packageId) {
          this.applyWashPriceFromSelection(index, options.packageId, options.preservedPrice);
        }
      });
  }

  private buildWashNameOptions(packages: Package[]): {
    options: SelectOption[];
    meta: Map<string, WashNameSelectionMeta>;
  } {
    const options: SelectOption[] = [];
    const meta = new Map<string, WashNameSelectionMeta>();

    for (const pkg of packages) {
      const activeVariants = (pkg.variants ?? []).filter(variant => variant.is_active !== false);

      if (activeVariants.length > 0) {
        activeVariants.forEach((variant, variantIndex) => {
          const value = this.encodeWashNameValue(pkg.id, variantIndex, variant.id);
          const label = `${pkg.package_name} - ${variant.variant_name}`.trim();
          options.push({ label, value });
          meta.set(value, { package: pkg, variant });
        });
        continue;
      }

      options.push({ label: pkg.package_name, value: pkg.id });
      meta.set(pkg.id, { package: pkg });
    }

    return { options, meta };
  }

  private encodeWashNameValue(packageId: string, variantIndex: number, variantId?: string): string {
    const variantKey = variantId?.trim() ? variantId.trim() : `i:${variantIndex}`;
    return `${packageId}::${variantKey}`;
  }

  private getWashSelectionMeta(
    index: number,
    selectionValue: string | null
  ): WashNameSelectionMeta | null {
    if (!selectionValue) {
      return null;
    }

    const fromMap = this.washSelectionMetaByIndex[index]?.get(selectionValue);
    if (fromMap) {
      return fromMap;
    }

    const pkg = this.packagesByIndex[index]?.find(p => p.id === selectionValue);
    return pkg ? { package: pkg } : null;
  }

  private setupComputedTotals(): void {
    this.items.controls.forEach(group => {
      const washGroup = group as FormGroup;
      ['number_of_washes', 'wash_price'].forEach(name => {
        washGroup
          .get(name)
          ?.valueChanges.pipe(takeUntil(this.destroy$))
          .subscribe(() => this.recalcWashTotals());
      });
    });
    this.recalcWashTotals();
  }

  private recalcWashTotals(): void {
    let sum = 0;
    this.items.controls.forEach(group => {
      const washGroup = group as FormGroup;
      const raw = washGroup.getRawValue();
      const lineTotal = this.computeLineTotal(raw.number_of_washes, raw.wash_price);
      washGroup.patchValue({ wash_total: lineTotal }, { emitEvent: false });
      sum += lineTotal ?? 0;
    });
    this.prepaidForm.patchValue({ total_wash_price: sum || null }, { emitEvent: false });
  }

  private computeLineTotal(countValue: unknown, priceValue: unknown): number | null {
    const count = this.parseNumber(countValue);
    const price = this.parseNumber(priceValue);
    if (count == null || price == null) {
      return null;
    }
    return Math.round(count * price * 100) / 100;
  }

  private parseNumber(value: unknown): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  }

  private setupWashNameResetOnTypeChange(): void {
    this.items.controls.forEach((group, index) => {
      const washGroup = group as FormGroup;
      washGroup
        .get('wash_type')
        ?.valueChanges.pipe(takeUntil(this.destroy$))
        .subscribe((washType: string | null) => {
          washGroup.patchValue({ wash_name: null, wash_price: null }, { emitEvent: false });
          this.resetWashPriceControl(index);
          this.markClearedWashFieldsAsUntouched(washGroup);
          this.loadWashNamesForIndex(index, washType);
        });
    });
  }

  /**
   * Forget prior interaction on the fields a wash-type change just emptied.
   *
   * Changing the type clears wash_name / wash_price, but `patchValue` leaves `dirty` and
   * `touched` from the earlier selection. The controls were then invalid *and* marked as
   * interacted-with, so "Wash Name is required" appeared under a dropdown the app itself had
   * just cleared, before staff touched it (CWS-506).
   *
   * Only the interaction state is reset — the validators still run, and `submitted` still forces
   * the messages once Save is pressed.
   */
  private markClearedWashFieldsAsUntouched(washGroup: FormGroup): void {
    ['wash_name', 'wash_price'].forEach(name => {
      const control = washGroup.get(name);
      control?.markAsPristine();
      control?.markAsUntouched();
    });
  }

  private setupWashPriceOnNameChange(): void {
    this.items.controls.forEach((group, index) => {
      const washGroup = group as FormGroup;
      washGroup
        .get('wash_name')
        ?.valueChanges.pipe(takeUntil(this.destroy$))
        .subscribe((packageId: string | null) => {
          this.applyWashPriceFromSelection(index, packageId);
        });
    });
  }

  private applyWashPriceFromSelection(
    index: number,
    selectionValue: string | null,
    preservedPrice?: number | null
  ): void {
    const washGroup = this.getWashGroup(index);
    const priceControl = washGroup.get('wash_price');

    if (!selectionValue) {
      this.allowsCustomPriceByIndex[index] = false;
      priceControl?.enable({ emitEvent: false });
      washGroup.patchValue({ wash_price: null }, { emitEvent: false });
      this.recalcWashTotals();
      return;
    }

    const selection = this.getWashSelectionMeta(index, selectionValue);
    if (!selection) {
      return;
    }

    const { package: pkg, variant } = selection;
    const allowsCustom = this.resolveAllowsCustomPrice(pkg, variant);
    this.allowsCustomPriceByIndex[index] = allowsCustom;

    if (allowsCustom) {
      priceControl?.enable({ emitEvent: false });
      washGroup.patchValue({ wash_price: preservedPrice ?? null }, { emitEvent: false });
    } else {
      const listPrice = this.resolveListPrice(pkg, variant);
      priceControl?.enable({ emitEvent: false });
      washGroup.patchValue({ wash_price: listPrice }, { emitEvent: false });
      priceControl?.disable({ emitEvent: false });
    }
    this.recalcWashTotals();
  }

  private resetWashPriceControl(index: number): void {
    this.allowsCustomPriceByIndex[index] = false;
    const priceControl = this.getWashGroup(index).get('wash_price');
    priceControl?.enable({ emitEvent: false });
    this.getWashGroup(index).patchValue({ wash_price: null }, { emitEvent: false });
  }

  private resolveListPrice(
    pkg: Package,
    variant?: NonNullable<Package['variants']>[number]
  ): number | null {
    if (variant) {
      return this.parseNumber(variant.single_list_price);
    }
    return this.parseNumber(pkg.single_list_price);
  }

  private resolveAllowsCustomPrice(
    pkg: Package,
    variant?: NonNullable<Package['variants']>[number]
  ): boolean {
    if (variant) {
      return this.isAllowsCustomPriceValue(variant.allows_custom_price);
    }
    return this.isAllowsCustomPriceValue(pkg.allows_custom_price);
  }

  private isAllowsCustomPriceValue(v: unknown): boolean {
    return v === true || v === 1 || v === '1' || v === 'true';
  }
}
